import ReactJson from "react-json-view";
import XMLViewer from "react-xml-viewer";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { MdDragIndicator } from "react-icons/md";
import { useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormInputText from "../shared/FormInputText";
import { AsyncPaginate } from "react-select-async-paginate";
import { loadAccountHccIdentificationNumber, loadSupplier } from "../../utils/optionLoaderUtils";
import FormDate from "../shared/FormDate";
import FormInputNumber from "../shared/FormInputNumber";
import FormAddress from "../shared/FormAddress";
import { ToWords } from "to-words";
import { fetchAddressByAddressKey, fetchAllBankAccounts, fetchBankAccountDetails } from "../../utils/apiUtils";
import useFormPersist from 'react-hook-form-persist'
import { convertDate, convertJsonToXml, downloadTxtFile } from "../../utils/commonUtils";

const toWords = new ToWords({
    localeCode: "en-US",
    converterOptions: {
        currency: true,
        ignoreDecimal: false,
        ignoreZeroCurrency: true,
        doNotAddOnly: false,
        currencyOptions: {
            // can be used to override defaults for the selected locale
            name: "Dollar",
            plural: "Dollars",
            symbol: "$",
            fractionalUnit: {
                name: "cent",
                plural: "cents",
                symbol: "",
            },
        },
    },
});

const addressStateSchema = z.object({
    label: z.string(),
    value: z.string(),
}).nullable().optional();

const addressCountrySchema = z.object({
    label: z.string(),
    value: z.string(),
}).nullable().optional();

const bankAddressSchema = z.object({
    address1: z.string().optional(),
    address2: z.string().optional(),
    address3: z.string().optional(),
    city: z.string().optional(),
    state: addressStateSchema,
    postalCode: z.string().optional(),
}).optional();

const payeeAddressSchema = z.object({
    address1: z.string().optional(),
    address2: z.string().optional(),
    address3: z.string().optional(),
    city: z.string().optional(),
    county: z.string().optional(),
    state: addressStateSchema,
    postalCode: z.string().optional(),
    country: addressCountrySchema,
}).optional();

const accountSchema = z.object({
    label: z.string().optional(),
    value: z.string().optional(),
    additional: z.object({
        accountHccId: z.string().optional(),
        accountName: z.string().optional(),
        taxAddressKey: z.string().optional(),
    }).optional(),
}).nullable().refine((val) => val !== null, {
    message: "Account is required",
});

const payeeSchema = z.object({
    label: z.string().optional(),
    value: z.string().optional(),
    additional: z.object({
        supplierHccId: z.string().optional(),
        supplierName: z.string().optional(),
        taxId: z.string().optional(),
        taxAddressKey: z.string().optional(),
    }).optional(),
}).nullable().refine((val) => val !== null, {
    message: "Payee is required",
});

const schema = z.object({
    payThruDate: z.date({
        required_error: "Pay Thru Date is required",
        invalid_type_error: "Pay Thru Date must be a valid date",
    }).nullable().optional(),
    account: accountSchema,
    checkingAccount: z.string().nullable().optional(),
    vendorId: z.string().nullable().optional(),
    fromBillingDate: z.date({
        required_error: "From Billing Date is required",
        invalid_type_error: "From Billing Date must be a valid date",
    }).nullable().optional(),
    thruBillingDate: z.date({
        required_error: "Thru Billing Date is required",
        invalid_type_error: "Thru Billing Date must be a valid date",
    }).nullable().optional(),
    checkAccountNumber: z.string({
        required_error: "Check Account Number is required",
    }).min(1, "Check Account Number is required").nullable().optional(),
    checkAccountId: z.string({
        required_error: "Check Account ID is required",
    }).min(1, "Check Account ID is required").nullable().optional(),
    checkNumber: z.string({
        required_error: "Check Number is required",
    }).min(1, "Check Number is required").nullable().optional(),
    bankName: z.string({
        required_error: "Bank Name is required",
    }).min(1, "Bank Name is required").nullable().optional(),
    bankAddress: bankAddressSchema,
    abaTransitNumberPart1: z.string().nullable().optional(),
    abaTransitNumberPart2: z.string().nullable().optional(),
    abaTransitNumberPart3: z.string().nullable().optional(),
    bankTransitCode: z.string().nullable().optional(),
    paymentDate: z.date({
        required_error: "Payment Date is required",
        invalid_type_error: "Payment Date must be a valid date",
    }).nullable().optional(),
    checkAmount: z.union([
        z.number({
            required_error: "Check Amount is required",
            invalid_type_error: "Check Amount must be a number",
        }).positive("Check Amount must be greater than 0"),
        z.string().transform((val) => {
            if (!val || val.trim() === '') throw new Error("Check Amount is required");
            const num = parseFloat(val);
            if (isNaN(num)) throw new Error("Check Amount must be a valid number");
            if (num <= 0) throw new Error("Check Amount must be greater than 0");
            return num;
        }),
    ]).nullable().optional(),
    payee: payeeSchema,
    payeeId: z.string().nullable().optional(),
    payeeTaxId: z.string().nullable().optional(),
    payeeName: z.string().nullable().optional(),
    payeeAddress: payeeAddressSchema,
    clientName: z.string().nullable().optional(),
    clientId: z.string().nullable().optional(),
    administratorFor: z.string().nullable().optional(),
    accountAddress: z.any().nullable().optional(),
}).refine((data) => {
    // If payee is selected, payeeId, payeeTaxId, and payeeName should be present
    if (data.payee) {
        return !!(data.payeeId && data.payeeTaxId && data.payeeName);
    }
    return true;
}, {
    message: "Payee information is incomplete",
    path: ["payee"],
});

const HrpCheckReportGenerator = () => {
    const [showJson, setShowJson] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        watch,
        reset,
    } = useForm({
        resolver: zodResolver(schema),
        mode: "onChange",
        defaultValues: {}
    });

    useFormPersist("myFormKey", {
        watch,
        setValue,
        storage: window.localStorage,
    });

    const watchJsonForm = useWatch({ control });

    const checkAmountInWords = toWords.convert(watchJsonForm?.checkAmount || 0, { currency: true });

    const account = watchJsonForm.account?.additional;
    const accountHccId = account?.accountHccId;

    const { isLoading, isFetching } = useQuery({
        queryKey: ['inputCheckingAccount', accountHccId],
        queryFn: async () => {

            const [
                addressResult,
                bankAccountsResult,
                bankDetailsResult
            ] = await Promise.all([
                fetchAddressByAddressKey(account.taxAddressKey),
                fetchAllBankAccounts({ accountName: accountHccId }),
                fetchBankAccountDetails({ pk: accountHccId, sk: 'bankInfo' })
            ]);

            const accountAddress = addressResult?.rows?.[0];
            const hrpBankInfo = bankAccountsResult?.rows?.[0];
            const bankDetails = bankDetailsResult?.Items?.[0];

            setValue('accountAddress', accountAddress);
            setValue('clientId', accountHccId);
            setValue('clientName', account?.accountName);

            setValue('checkingAccount', hrpBankInfo?.accountNumber);
            setValue('bankName', hrpBankInfo?.bankName || bankDetails?.bankname);
            setValue('checkAccountId', accountHccId);
            setValue('checkAccountNumber', hrpBankInfo?.accountNumber || bankDetails?.bankaccountnumber);

            if (bankDetails) {
                setValue('bankAddress.address1', bankDetails.address);
                setValue('bankAddress.city', bankDetails.city);
                setValue('bankAddress.state', { label: bankDetails.state, value: bankDetails.state });
                setValue('bankAddress.postalCode', bankDetails.zip);

                setValue('bankTransitCode', bankDetails.routingnumber);
                setValue('abaTransitNumberPart1', bankDetails.transitnumberpart1);
                setValue('abaTransitNumberPart2', bankDetails.transitnumberpart2);
                setValue('abaTransitNumberPart3', bankDetails.transitnumberpart3);
            } else {
                setValue('bankAddress.address1', '');
                setValue('bankAddress.city', '');
                setValue('bankAddress.state', null);
                setValue('bankAddress.postalCode', '');

                setValue('bankTransitCode', '');
                setValue('abaTransitNumberPart1', '');
                setValue('abaTransitNumberPart2', '');
                setValue('abaTransitNumberPart3', '');
            }

            return { ...hrpBankInfo, ...bankDetails };

        },

        enabled: !!accountHccId
    })

    const watchXmlForm = convertJsonToXml({
        _declaration: {
            _attributes: {
                version: "1.0",
            },
        },
        JavelinaReport: {
            _attributes: {
                name: "Check Report",
            },
            Source: {
                _attributes: {
                    address1: "5353 N 16th Street",
                    address2: "Suite 400",
                    city: "Pheonix",
                    state: "AZ",
                    phone: "602-604-3300",
                    company: "Mphasis Healthcare Solutions",
                    product: "Javelina",
                },
            },
            Input: {
                _attributes: {
                    aspid: "P323",
                    name: "Check Report",
                },
                Process: {
                    _attributes: {
                        class: "true",
                        query: "false",
                    },
                },
                Class: {
                    _attributes: {
                        name: "com.eldocomp.report.busobjs.billingandadmin.CheckWriter",
                        method: "process",
                    },
                },
                args: {
                    FromBlkOfBusID: {
                        _attributes: { type: "String" },
                        _text: 'CNTVO'
                    },
                    ThruBlkOfBusID: {
                        _attributes: { type: "String" },
                        _text: "CNTVO",
                    },
                    FromClientID: {
                        _attributes: { type: "String" },
                        _text: "CNTVO",
                    },
                    ThruClientID: {
                        _attributes: { type: "String" },
                        _text: "CNTVO",
                    },
                    FromLocationName: {
                        _attributes: { type: "String" },
                        _text: "",
                    },
                    ThruLocationName: {
                        _attributes: { type: "String" },
                        _text: "",
                    },
                    ClientType: {
                        _attributes: { type: "String" },
                        _text: "S",
                    },
                    CombineByTaxID: {
                        _attributes: { type: "String" },
                        _text: "N",
                    },
                    FromPayeeBrokerID: {
                        _attributes: { type: "String" },
                        _text: "",
                    },
                    ThruPayeeBrokerID: {
                        _attributes: { type: "String" },
                        _text: "",
                    },
                    FromBrokerID: {
                        _attributes: { type: "String" },
                        _text: "CJ",
                    },
                    ThruBrokerID: {
                        _attributes: { type: "String" },
                        _text: "TG",
                    },
                    BrokerType: {
                        _attributes: { type: "String" },
                        _text: "G",
                    },
                    PayThruDate: {
                        _attributes: { type: "String" },
                        _text: (() => {
                            const date = watchJsonForm?.payThruDate;
                            if (!date) return undefined;
                            if (date instanceof Date) return date.toISOString().slice(0, 10);
                            if (typeof date === "string") return convertDate(date, "YYYY-MM-DD");
                            return convertDate(date, "YYYY-MM-DD");
                        })(),
                    },
                    FromBillingDate: {
                        _attributes: { type: "String" },
                        _text: convertDate(watchJsonForm?.fromBillingDate, "YYYYMMDD"),
                    },
                    ThruBillingDate: {
                        _attributes: { type: "String" },
                        _text: convertDate(watchJsonForm?.thruBillingDate, "YYYYMMDD"),
                    },
                    CheckingAccount: {
                        _attributes: { type: "String" },
                        _text: watchJsonForm?.checkingAccount,
                    },
                    Funding: {
                        _attributes: { type: "String" },
                        _text: 'N',
                    },
                    MinimumCheckAmount: {
                        _attributes: { type: "String" },
                        _text: "0.01",
                    },
                    PaymentDescription: {
                        _attributes: { type: "String" },
                        _text: "",
                    },
                    ParameterSetName: {
                        _attributes: { type: "String" },
                        _text: watchJsonForm?.account?.additional?.accountName,
                    },
                    GeneratePDF: {
                        _attributes: { type: "String" },
                        _text: "R",
                    },
                    Vendor_id: {
                        _attributes: { type: "String" },
                        _text: "41"
                    },
                },
            },
            Output: {
                _attributes: {
                    vendor_id: "41",
                    form_name: "ACHK001",
                    aspid: "P323",
                    type: "file",
                    fileType: "xml",
                    directory: "/Javelina-4/app/p323/batch/scheduler/bin/../../reports",
                    filename: "BrokerComp",
                },

                CheckList: {
                    CheckExtractFile: {
                        CheckInfo: {
                            FirstLogoImgURL: {},
                            FirstSignImgURL: {},
                            SecondSignImgURL: {},
                            CheckNumber: {
                                _text: watchJsonForm?.checkNumber,
                            },
                            CheckType: {},
                            CheckPurpose: {},
                            CheckFormat: {},
                            CheckAccountID: {
                                _text: watchJsonForm?.account?.value,
                            },
                            CheckAccountNumber: {
                                _text: watchJsonForm?.checkAccountNumber,
                            },
                            BankInfo: {
                                BankName: {
                                    _text: watchJsonForm?.bankName,
                                },
                                BankAddress: {
                                    Address1: {
                                        _text: watchJsonForm?.bankAddress?.address1,
                                    },
                                    Address2: {
                                        _text: watchJsonForm?.bankAddress?.address2,
                                    },
                                    Address3: { _text: watchJsonForm?.bankAddress?.address3 },
                                    City: {
                                        _text: watchJsonForm?.bankAddress?.city,
                                    },
                                    State: {
                                        _text: watchJsonForm?.bankAddress?.state?.value,
                                    },
                                    PostalCode: {
                                        _text: watchJsonForm?.bankAddress?.postalCode,
                                    },
                                },
                            },
                            BlockOfBusinessInfo: {
                                BlockOfBusiness: {
                                    _text: "PCP01",
                                },
                                BobName: {
                                    _text: "Self-Funded Plan",
                                },
                                BlockAddress: {
                                    Address1: {
                                        _text: '199 Scott St' //watchJsonForm?.accountAddress?.addressLine
                                    },
                                    Address2: {
                                        _text: '8th Floor' // watchJsonForm?.accountAddress?.addressLine2
                                    },
                                    Address3: {
                                        _text: '' //watchJsonForm?.accountAddress?.addressLine3
                                    },
                                    City: {
                                        _text: 'Buffalo' //watchJsonForm?.accountAddress?.cityName
                                    },
                                    State: {
                                        _text: 'NY' //watchJsonForm?.accountAddress?.stateCode
                                    },
                                    PostalCode: {
                                        _text: '14204' //watchJsonForm?.accountAddress?.zipCode
                                    },
                                },
                            },
                            PayTo: {
                                _text: "BROKER",
                            },
                            PayViaEFT: {
                                _text: "Y",
                            },
                            PayeeInfo: {
                                PayeeName: {
                                    _text: watchJsonForm?.payeeName?.replace('Finance Only ', ''),
                                },
                                PayeeID: {
                                    _text: watchJsonForm.payeeId,
                                },
                                PayeeAddress: {
                                    Address1: {
                                        _text: watchJsonForm?.payeeAddress?.address1,
                                    },
                                    Address2: { _text: watchJsonForm?.payeeAddress?.address2 },
                                    Address3: { _text: watchJsonForm?.payeeAddress?.address3 },
                                    City: {
                                        _text: watchJsonForm?.payeeAddress?.city,
                                    },
                                    County: {
                                        _text: watchJsonForm?.payeeAddress?.county,
                                    },
                                    State: {
                                        _text: watchJsonForm?.payeeAddress?.state?.value,
                                    },
                                    PostalCode: {
                                        _text: watchJsonForm?.payeeAddress?.postalCode,
                                    },
                                    Country: {
                                        _text: watchJsonForm?.payeeAddress?.country?.value || ''
                                    },
                                },
                                PayeeTaxID: {
                                    _text: watchJsonForm?.payeeTaxId,
                                },
                            },
                            RoutingCodes: {},
                            ABATransitNumberPart1: { _text: watchJsonForm?.abaTransitNumberPart1 },
                            ABATransitNumberPart2: { _text: watchJsonForm?.abaTransitNumberPart2 },
                            ABATransitNumberPart3: { _text: watchJsonForm?.abaTransitNumberPart3 },
                            BankTransitCode: { _text: watchJsonForm?.bankTransitCode },
                            CheckAmount: { _text: watchJsonForm?.checkAmount },
                            CheckCurrency: { _text: "US$" },
                            CheckAmountInWords: { _text: checkAmountInWords },
                            PaymentDate: { _text: convertDate(watchJsonForm?.paymentDate, "YYYYMMDD") },
                            AlphaPaymentAmount: {},
                            MicrCode: {
                                BankTransitCode: { _text: watchJsonForm?.bankTransitCode },
                                CheckAccountNumber: { _text: watchJsonForm?.checkAccountNumber, },
                                CheckNumber: { _text: watchJsonForm?.checkNumber, },
                            },
                            Instrument1: {},
                            Instrument2: {},
                            Instrument3: {},
                            ClientName: { _text: watchJsonForm?.clientName },
                            ClientID: { _text: watchJsonForm?.clientId },
                            AdministratorFor: { _text: watchJsonForm?.administratorFor },
                            LineInfo: {
                                LineItemInfo: {
                                    LineAmount: {},
                                    LineDescription: {},
                                },
                            },
                        },
                        ClientIDs: { _text: watchJsonForm?.clientId },
                        ClientDetails: {
                            Client: {
                                ClientID: { _text: watchJsonForm?.clientId },
                                ClientName: { _text: watchJsonForm?.clientName },
                            },
                        },
                        LocationDetails: {},
                    },
                },
            },
        },
    });

    const onSubmit = async (data) => {
        // Handle form submission if needed
    };

    return (
        <div className={"container "}>
            <p className="title">HRP Check Report Generator</p>
            <p className="sub-title">Generate and download the XML file</p>

            <div style={{ display: 'flex', gap: '1rem' }}>

                <p className="success" style={{ cursor: 'pointer', color: 'var(--green)' }} onClick={() => {
                    setShowJson(!showJson)
                }}>{showJson ? 'Hide Json' : 'View Json'}</p>

                <p className="danger" style={{ cursor: 'pointer' }} onClick={() => {
                    // Clear localStorage used by useFormPersist
                    window.localStorage.removeItem('myFormKey');
                    // Reset all form fields to default values
                    reset({
                        payThruDate: undefined,
                        account: null,
                        checkingAccount: '',
                        vendorId: '',
                        fromBillingDate: undefined,
                        thruBillingDate: undefined,
                        checkAccountNumber: '',
                        checkAccountId: '',
                        checkNumber: '',
                        bankName: '',
                        bankAddress: {
                            address1: '',
                            address2: '',
                            address3: '',
                            city: '',
                            state: null,
                            postalCode: '',
                        },
                        abaTransitNumberPart1: '',
                        abaTransitNumberPart2: '',
                        abaTransitNumberPart3: '',
                        bankTransitCode: '',
                        paymentDate: undefined,
                        checkAmount: undefined,
                        payee: null,
                        payeeId: '',
                        payeeTaxId: '',
                        payeeName: '',
                        payeeAddress: {
                            address1: '',
                            address2: '',
                            address3: '',
                            city: '',
                            county: '',
                            state: null,
                            postalCode: '',
                            country: null,
                        },
                        clientName: '',
                        clientId: '',
                        administratorFor: '',
                        accountAddress: null,
                    });
                }}>Clear</p>
            </div>


            <div style={{ display: "flex", gap: "1rem", height: "100%", overflow: "scroll" }}>
                <PanelGroup
                    autoSaveId="review"
                    direction="horizontal"
                    style={{
                        height: "100%",
                        overflowY: "scroll",
                    }}
                >
                    <Panel
                        defaultSize={40}
                    >

                        <form
                            className="form"
                            onSubmit={handleSubmit(onSubmit)}
                            style={{
                                overflowY: "scroll",
                                scrollbarWidth: "1rem",
                                padding: "1rem",
                                flexGrow: 1,
                                width: "100%",
                                height: "100%",
                                position: "relative",
                            }}
                        >
                            <div>
                                <p style={{ padding: "0rem 1rem 1rem 0rem", fontWeight: 600 }}>Pay Date</p>
                                <FormDate label={"Thru"} name={"payThruDate"} control={control} errors={errors} showCurrentTZ={false} />
                            </div>

                            <div className="form-input">
                                <label style={{ display: "block" }}>Account ID</label>
                                <Controller
                                    control={control}
                                    name="account"
                                    render={({ field }) => (
                                        <AsyncPaginate
                                            menuPlacement="bottom"
                                            isClearable
                                            debounceTimeout={300}
                                            name={field.name}
                                            inputRef={field.ref}
                                            classNamePrefix="react-select"
                                            value={field.value}
                                            onChange={(val) => {
                                                field.onChange(val);
                                                if (!val) {
                                                    setValue('checkingAccount', null);
                                                }
                                            }}
                                            loadOptions={loadAccountHccIdentificationNumber}
                                        />
                                    )}
                                />
                                {errors.account && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.account.message}</p>}
                            </div>

                            {/* <div style={{ display: 'flex', gap: '1rem' }}>
                                <FormInputText label={"Checking Account"} register={register} name={"checkingAccount"} errors={errors} placeholder="Checking Account" />
                                <FormInputText label={"Vendor ID"} register={register} name={"vendorId"} errors={errors} placeholder="VendorId" />
                            </div>

                            <div>
                                <p style={{ padding: "0rem 1rem 1rem 0rem", fontWeight: 600 }}>Billing Date</p>
                                <div style={{ display: "flex", gap: "1rem" }}>
                                    <FormDate label={"From"} name={"fromBillingDate"} control={control} errors={errors} showCurrentTZ={false} />
                                    <FormDate label={"Thru"} name={"thruBillingDate"} control={control} errors={errors} showCurrentTZ={false} />
                                </div>
                            </div> */}

                            <p style={{ padding: "2rem 1rem 2rem 0rem", fontWeight: 600, fontSize: "1.3rem" }}>Output Check List</p>
                            {(isLoading || isFetching) && <div className="loader-wrapper" style={{ height: '1rem' }}> <div className="loader" /></div>}
                            <p style={{ padding: "0rem 1rem 1rem 0rem", fontWeight: 600 }}>Check Info</p>

                            <div style={{ display: "flex", gap: "1rem", justifyContent: "space-between" }}>
                                <FormInputText label={"Check Account Number"} register={register} name={"checkAccountNumber"} errors={errors} placeholder="Check Account Number" />
                                <FormInputText label={"Check Account ID"} register={register} name={"checkAccountId"} errors={errors} placeholder="Check Account ID" />
                                <FormInputText label={"Check Number"} register={register} name={"checkNumber"} errors={errors} placeholder="Check Number" />
                            </div>

                            <p style={{ padding: "0rem 1rem 1rem 0rem", fontWeight: 600 }}>Bank Info</p>

                            <FormInputText label={"Bank Name"} register={register} name={"bankName"} errors={errors} placeholder="Bank Name" />
                            <br />
                            <FormAddress title={"Bank Address"} register={register} errors={errors} baseName={"bankAddress"} control={control} excludes={["county", "country"]} />

                            <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", padding: "2rem 0rem 0rem 0rem" }}>
                                <FormInputText label={"ABATransitNumberPart1"} register={register} name={"abaTransitNumberPart1"} errors={errors} placeholder="ABATransitNumberPart1" />
                                <FormInputText label={"ABATransitNumberPart2"} register={register} name={"abaTransitNumberPart2"} errors={errors} placeholder="ABATransitNumberPart2" />
                                <FormInputText label={"ABATransitNumberPart3"} register={register} name={"abaTransitNumberPart3"} errors={errors} placeholder="ABATransitNumberPart3" />
                            </div>

                            <FormInputText label={"BankTransitCode"} register={register} name={"bankTransitCode"} errors={errors} placeholder="Bank Transit Code" />

                            <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
                                <FormDate label={"Payment Date"} name={"paymentDate"} control={control} errors={errors} showCurrentTZ={false} />
                                <FormInputNumber label={"Check Amount"} register={register} name={"checkAmount"} errors={errors} placeholder="Check amount" helperText={checkAmountInWords} />
                            </div>


                            <p style={{ padding: "1rem 1rem 1rem 0rem", fontWeight: 600 }}>Payee Info</p>

                            <div className="form-input">
                                <label style={{ display: "block" }}>Payee ID</label>
                                <Controller
                                    control={control}
                                    name="payee"
                                    render={({ field }) => (
                                        <AsyncPaginate
                                            menuPlacement="bottom"
                                            isClearable
                                            debounceTimeout={300}
                                            name={field.name}
                                            inputRef={field.ref}
                                            classNamePrefix="react-select"
                                            value={field.value}
                                            onChange={async (val) => {
                                                field.onChange(val);
                                                if (!val) {
                                                    setValue('payeeId', '');
                                                    setValue('payeeTaxId', '');
                                                } else {
                                                    setValue('payeeId', val?.additional?.supplierHccId);
                                                    setValue('payeeTaxId', val?.additional?.taxId);
                                                    setValue('payeeName', val?.additional?.supplierName);
                                                    const addressResult = await fetchAddressByAddressKey(val?.additional?.taxAddressKey);

                                                    const address = addressResult?.rows?.[0];
                                                    if (address) {
                                                        setValue('payeeAddress.address1', address?.addressLine);
                                                        setValue('payeeAddress.address2', address?.addressLine2);
                                                        setValue('payeeAddress.address3', address?.addressLine3);
                                                        setValue('payeeAddress.city', address?.cityName);
                                                        setValue('payeeAddress.state', { label: address?.stateCode, value: address?.stateCode });
                                                        setValue('payeeAddress.county', address?.countyCode);
                                                        setValue('payeeAddress.postalCode', address?.zipCode);
                                                        setValue('payeeAddress.country', { label: address?.countryName, value: address?.countryCode });
                                                    } else {
                                                        setValue('payeeAddress.address1', null);
                                                        setValue('payeeAddress.address2', null);
                                                        setValue('payeeAddress.address3', null);
                                                        setValue('payeeAddress.city', null);
                                                        setValue('payeeAddress.state', null);
                                                        setValue('payeeAddress.county', null);
                                                        setValue('payeeAddress.postalCode', null);
                                                        setValue('payeeAddress.country', null);
                                                    }
                                                }
                                            }}
                                            loadOptions={loadSupplier}
                                        />
                                    )}
                                />
                                {errors.payee && <p style={{ color: "var(--red)", paddingTop: ".4rem" }}>{errors.payee.message}</p>}
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
                                <FormInputText label={"Payee Tax Id"} register={register} name={"payeeTaxId"} errors={errors} placeholder="Payee Tax ID" />
                            </div>

                            <FormInputText label={"Payee Name"} register={register} name={"payeeName"} errors={errors} placeholder="Payee Name" />
                            <br />
                            <FormAddress title={"Payee Address"} register={register} errors={errors} baseName={"payeeAddress"} control={control} />


                            <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", padding: "2rem 0rem 0rem 0rem" }}>
                                <FormInputText label={"ClientName"} register={register} name={"clientName"} errors={errors} placeholder="Client Name" />
                                <FormInputText label={"ClientId"} register={register} name={"clientId"} errors={errors} placeholder="Client Id" />
                                <FormInputText label={"AdministratorFor"} register={register} name={"administratorFor"} errors={errors} placeholder="AdministratorFor" />
                            </div>
                        </form>

                    </Panel>

                    <PanelResizeHandle className="resize-handle" style={{ height: "100%", }}>
                        <div style={{ justifyContent: "center", display: "flex", alignItems: "center", height: "100%" }}>
                            <MdDragIndicator size={"1.2rem"} />
                        </div>
                    </PanelResizeHandle>


                    <DisplayJSONandXaml xml={watchXmlForm} json={watchJsonForm} showJson={showJson} />

                </PanelGroup>

            </div>
        </div>
    );
};

export default HrpCheckReportGenerator;

const DisplayJSONandXaml = ({ xml, json, showJson }) => {
    // 1. Initialize state for the filename
    const [editableFileName, setEditableFileName] = useState("");

    // 2. Set the default filename when the component mounts or dates change
    useEffect(() => {
        const paymentDate = convertDate(json?.paymentDate, "YYYYMMDD") || convertDate(Date.now(), "YYYYMMDD");
        setEditableFileName(`1422JA${paymentDate}RA1E0A1D11.xml`);
    }, [json?.paymentDate]);

    return (
        <>
            {showJson && (
                <Panel defaultSize={30}>
                    {json && (
                        <ReactJson
                            style={{ padding: "1rem", overflowY: "scroll", height: "100%" }}
                            src={json}
                            name={false}
                            collapsed={1}
                            collapseStringsAfterLength={150}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            sortKeys={true}
                        />
                    )}
                </Panel>
            )}

            <Panel defaultSize={30}>
                {xml && (
                    <div style={{ padding: "1rem", overflowY: "scroll", height: "100%", alignItems: 'center', justifyContent: 'center' }}>
                        <XMLViewer xml={xml} indentSize={12} collapsible={true} initialCollapsedDepth={10} showLineNumbers={true} />
                        <br />
                        <div style={{
                            display: 'flex',
                            gap: '1rem',
                            alignItems: 'flex-end', // This aligns the button with the input box, not the label
                            width: '100%',
                            marginBottom: "10px"
                        }}>
                            <div style={{ flex: 1 }}> {/* flex: 1 allows the input to take available space */}
                                <label style={{
                                    fontSize: "12px",
                                    display: "block",
                                    marginBottom: "4px", // Adds a tiny gap between label and input
                                    fontWeight: "500",
                                    color: "#666"
                                }}>
                                    File Name
                                </label>
                                <input
                                    type="text"
                                    value={editableFileName}
                                    onChange={(e) => setEditableFileName(e.target.value)}
                                    style={{
                                        padding: "6px 10px",
                                        width: "100%",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        boxSizing: "border-box", // Ensures padding doesn't break width
                                        fontSize: "14px"
                                    }}
                                />
                            </div>

                            <button
                                className="btn"
                                style={{
                                    height: '32px', // Matches the height of the input (padding + font + border)
                                    padding: '0 15px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => downloadTxtFile(xml, editableFileName)}
                            >
                                Download
                            </button>
                        </div>

                    </div>
                )}
            </Panel>
        </>
    );
};
